/**
 * DashboardMobileFirst — variante mobile-first
 *
 * Design: colonna singola, cards grandi, statistiche leggibili con un pollice.
 * Nessun grid multi-colonna — tutto stacked verticalmente.
 * Su schermi più larghi le card si allargano ma rimangono in colonna.
 */
import { Card, Statistic, Space, Spin, Empty, Alert, Tag, Divider } from 'antd'
import {
  CarOutlined,
  UserOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import { useAuth } from '@/contexts/AuthContext'
import { usePermission } from '@/hooks/usePermission'
import { useCMMStats } from '@/hooks/useCMM'
import type { DashboardSummary } from '@/types'
import { INTERVENTION_STATUS_HEX as statusColors } from '@/constants/statuses'

// Card statistica grande, ottimizzata per touch
function StatCard({
  title,
  value,
  icon,
  color,
  onClick,
}: {
  title: string
  value: number
  icon: React.ReactNode
  color: string
  onClick?: () => void
}) {
  return (
    <Card
      onClick={onClick}
      style={{
        borderRadius: 12,
        cursor: onClick ? 'pointer' : 'default',
        borderLeft: `4px solid ${color}`,
      }}
      styles={{ body: { padding: '20px 24px' } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Statistic
          title={<span style={{ fontSize: 14 }}>{title}</span>}
          value={value}
          prefix={<span style={{ color, fontSize: 22 }}>{icon}</span>}
          valueStyle={{ color, fontSize: 32, fontWeight: 700 }}
        />
        {onClick && <ArrowRightOutlined style={{ color: '#bbb', fontSize: 18 }} />}
      </div>
    </Card>
  )
}

const CMMDashboardMobileFirst = () => {
  const navigate = useNavigate()
  const { data: stats, isLoading, error } = useCMMStats()

  if (isLoading) return <Spin size="large" style={{ display: 'block', margin: '48px auto' }} />
  if (error) return <Alert message="Errore caricamento dashboard CMM" type="error" showIcon style={{ margin: 16 }} />
  if (!stats) return <Empty description="Nessun dato disponibile" style={{ marginTop: 48 }} />

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <h2 style={{ margin: '8px 0 4px', fontSize: 22, fontWeight: 700 }}>Dashboard CMM</h2>

      <StatCard
        title="Da Prendere in Carico"
        value={stats.work_orders_approvate}
        icon={<FileTextOutlined />}
        color="#1890ff"
        onClick={() => navigate('/schede-lavoro?stato=in%20approvazione')}
      />
      <StatCard
        title="In Lavorazione"
        value={stats.work_orders_in_lavorazione}
        icon={<ToolOutlined />}
        color="#faad14"
        onClick={() => navigate('/schede-lavoro?stato=in%20lavorazione')}
      />
      <StatCard
        title="Clienti Totali"
        value={stats.customers_total}
        icon={<UserOutlined />}
        color="#52c41a"
      />
      <StatCard
        title="Veicoli Totali"
        value={stats.vehicles_total}
        icon={<CarOutlined />}
        color="#722ed1"
      />

      <Divider style={{ margin: '8px 0' }} />

      <Card
        title={
          <Space>
            <ClockCircleOutlined />
            <span>Interventi Meccanici</span>
            <Tag color="blue">{stats.interventi_totali}</Tag>
          </Space>
        }
        style={{ borderRadius: 12 }}
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {stats.interventi_per_stato.map((s: any) => (
            <div
              key={s.nome}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <span style={{ fontSize: 15 }}>{s.nome}</span>
              <span
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: statusColors[s.nome] || '#595959',
                  minWidth: 40,
                  textAlign: 'right',
                }}
              >
                {s.totale}
              </span>
            </div>
          ))}
          {stats.interventi_senza_stato > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
              <span style={{ fontSize: 15, color: '#d46b08' }}>Da assegnare</span>
              <span style={{ fontSize: 20, fontWeight: 700, color: '#d46b08' }}>
                {stats.interventi_senza_stato}
              </span>
            </div>
          )}
        </Space>
      </Card>
    </Space>
  )
}

const GeneralDashboardMobileFirst = () => {
  const navigate = useNavigate()
  const { data: summary, isLoading, error } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: async () => {
      const response = await axiosInstance.get<DashboardSummary>(API_ENDPOINTS.DASHBOARD_SUMMARY)
      return response.data
    },
    retry: 1,
    staleTime: 5 * 60 * 1000,
  })

  if (isLoading) return <Spin size="large" style={{ display: 'block', margin: '48px auto' }} />
  if (error) return <Alert message="Errore caricamento dashboard" type="error" showIcon style={{ margin: 16 }} />
  if (!summary?.stats) return <Empty description="Nessun dato disponibile" style={{ marginTop: 48 }} />

  const s = summary.stats

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <h2 style={{ margin: '8px 0 4px', fontSize: 22, fontWeight: 700 }}>Dashboard</h2>

      <StatCard
        title="Accettazioni Aperte"
        value={s.work_orders_open || 0}
        icon={<ToolOutlined />}
        color="#1890ff"
        onClick={() => navigate('/schede-lavoro?stato=bozza')}
      />
      <StatCard
        title="In Lavorazione"
        value={s.work_orders_in_progress || 0}
        icon={<ToolOutlined />}
        color="#faad14"
        onClick={() => navigate('/schede-lavoro?stato=in_lavorazione')}
      />
      <StatCard
        title="Clienti Totali"
        value={s.customers_total || 0}
        icon={<UserOutlined />}
        color="#52c41a"
        onClick={() => navigate('/customers')}
      />
      <StatCard
        title="Veicoli Totali"
        value={s.vehicles_total || 0}
        icon={<CarOutlined />}
        color="#722ed1"
        onClick={() => navigate('/vehicles')}
      />

      {!!s.parts_low_stock && (
        <>
          <Divider style={{ margin: '4px 0' }} />
          <StatCard
            title="Ricambi in Esaurimento"
            value={s.parts_low_stock}
            icon={<CheckCircleOutlined />}
            color="#ff4d4f"
            onClick={() => navigate('/parts')}
          />
          <StatCard
            title="Auto Cortesia Disponibili"
            value={s.courtesy_cars_available || 0}
            icon={<CarOutlined />}
            color="#52c41a"
            onClick={() => navigate('/courtesy-cars')}
          />
        </>
      )}
    </Space>
  )
}

export function DashboardMobileFirst() {
  const isOperativo = usePermission('work_orders.operativo')
  const isFullView  = usePermission('dashboard.full_view')
  if (isOperativo && !isFullView) return <CMMDashboardMobileFirst />
  return <GeneralDashboardMobileFirst />
}

export default DashboardMobileFirst
